import { trackIt, getSessionStorage, setSessionStorage, getSiteStructure, getLoginData, getCustomerFlow } from '@vfde-fe/sails_core';
import {
    selectHasSubscription,
    selectSalesChannel,
    selectSubscriptionId,
} from '../../Tariff/selectors';
import { all, call, select } from 'redux-saga/effects';
import { selectAtomicDevice } from '../selectors';
import {
    selectHardwareOnly,
    selectName,
    selectOffer,
    selectVendor,
} from '../../DeviceDetail/selectors';
import {
    ADDITIONAL_PAGE_OPTIONS,
    BTX_BNT,
    TRACKING_BUSINESS_TRANSACTION_HARDWARE_ONLY,
    TRACKING_FLOWTYPE_HARDWARE_ONLY,
    TRACKING_FLOWTYPE_PRODUCT_DETAILS,
    TRACKING_FLOWTYPE_STORAGE,
    TRACKING_FLOWTYPE_TARIFE,
    TRACKING_HARDWARE_ONLY,
    TRACKING_LOGGED_IN,
    TRACKING_NEW_CONTRACT,
    TRACKING_NOT_LOGGED_IN,
    TRACKING_PAGE_TYPE_PRODUCT_DETAIL,
    TRACKING_PLATFORM_TYPE_RESPONSIVE,
    TRACKING_BUNDLE,
    TRACKING_PRODUCT_LINE,
    TRACKING_PRODUCT_TYPE,
    TRACKING_DEVICE,
    TRACKING_SITE_AREA_MOBILITY,
    TRACKING_TARIFF,
    TRACK_PAGE_VIEW,
    SalesChannel,
    SALESCHANNEL_FAMILY_FRIENDS,
    SALESCHANNEL_SOHO,
    SALESCHANNEL_REDPLUS,
} from '@vfde-fe/sails-constants';
import {IaProducts} from '../interface';

/**
 * Track page view
 */
 export function* trackPageView () {
    const [
        activeSubId,
        activeOffer,
        deviceName,
        vendor,
        atomicDevice,
        isHardwareOnly,
        salesChannel,
        hasSubscription,
    ] =
    yield all([
        select(selectSubscriptionId()),
        select(selectOffer()),
        select(selectName()),
        select(selectVendor()),
        select(selectAtomicDevice()),
        select(selectHardwareOnly()),
        select(selectSalesChannel()),
        select(selectHasSubscription()),
    ]);
    const { pageName } = window[ADDITIONAL_PAGE_OPTIONS];

    const flowType = yield getFlowTypeStorage(hasSubscription, salesChannel);

    const trackingData = {
        activeSubId,
        activeOffer,
        pageName,
        salesChannel,
        deviceName,
        vendor,
        flowType,
        atomicDevice,
        isHardwareOnly,
        hasSubscription,
    };

    if (trackingData.atomicDevice) {
        const trackingPayload = createTrackingPageView(trackingData);
        trackIt(TRACK_PAGE_VIEW, trackingPayload);
    }
}

/**
 * Get flowtype from storage
 *
 * @param hasSubscription
 * @param salesChannel
 */
 export function* getFlowTypeStorage (hasSubscription, salesChannel: SalesChannel) {
    let flowType = yield call(getSessionStorage, TRACKING_FLOWTYPE_STORAGE);

    // FlowType is empty object when not set. That's why this condition is written like this.
    if (flowType && Object.keys(flowType).length === 0) {
        flowType = hasSubscription ? TRACKING_FLOWTYPE_TARIFE : TRACKING_FLOWTYPE_PRODUCT_DETAILS;

        switch (salesChannel) {
            case SALESCHANNEL_FAMILY_FRIENDS:
                flowType = `${flowType} fuf`;
                break;
            case SALESCHANNEL_SOHO:
                flowType = `${flowType} soho`;
                break;
            case SALESCHANNEL_REDPLUS:
                flowType = `${flowType} redplus`;
                break;
        }

        yield call(setSessionStorage, TRACKING_FLOWTYPE_STORAGE, flowType);
    }

    return flowType;
}

/**
 * Create the page view tracking object
 * @param {object} trackingData - Tracking Data
 */
export const createTrackingPageView = (trackingData) => {
    const parser = new DOMParser();
    const siteStructure = getSiteStructure(trackingData.pageName, BTX_BNT);
    const loginStatus = getLoginData() ? TRACKING_LOGGED_IN : TRACKING_NOT_LOGGED_IN;
    const lineOfCustomer = getCustomerFlow(trackingData.salesChannel) ;
    const productCategory = trackingData.isHardwareOnly ? TRACKING_DEVICE : TRACKING_BUNDLE;
    const {flowType, atomicDevice} = trackingData;
    const devicePriceOnce = trackingData.isHardwareOnly ? atomicDevice?.totalPrice?.gross : atomicDevice?.oneTimePrice?.gross;
    const {atomicName} = atomicDevice;
    let parsedAtomicName = parser.parseFromString(atomicName, 'text/html').body.firstChild.textContent;

    const aProducts: IaProducts[] = [{
        sku: atomicDevice.backendId,
        name: parsedAtomicName,
        type: TRACKING_DEVICE,
        priceOnce: devicePriceOnce,
        proposition: 1,
        units: 1,
    }];

    if (trackingData.hasSubscription && !trackingData.isHardwareOnly) {
        aProducts.push({
            sku : trackingData.activeSubId.toString(),
            name : trackingData.activeOffer.name,
            type : TRACKING_TARIFF,
            priceOnce : trackingData.activeOffer?.oneTimeFee?.gross.toString(),
            priceMonthly : trackingData.activeOffer?.tariffPrice?.gross.toString(),
            duration : 24,
            proposition : 1,
            units : 1,
        });

        parsedAtomicName += ` | ${trackingData.activeOffer.name}`;
    }

    const trackingPayload = {
        siteStructure,
        loginStatus,
        flowType: trackingData.isHardwareOnly ? TRACKING_FLOWTYPE_HARDWARE_ONLY : flowType,
        lineOfCustomer,
        pageType: TRACKING_PAGE_TYPE_PRODUCT_DETAIL,
        siteArea: TRACKING_SITE_AREA_MOBILITY,
        platformType: TRACKING_PLATFORM_TYPE_RESPONSIVE,
        businessTransactionType: trackingData.isHardwareOnly ? TRACKING_BUSINESS_TRANSACTION_HARDWARE_ONLY : TRACKING_NEW_CONTRACT,
        oOrder: {
            aProducts,
            aPropositions: [{
                id: 1,
                name: parsedAtomicName,
                targetAudience: lineOfCustomer,
                retention: trackingData.isHardwareOnly ? TRACKING_HARDWARE_ONLY : TRACKING_NEW_CONTRACT,
                productType: trackingData.isHardwareOnly ? TRACKING_HARDWARE_ONLY : TRACKING_PRODUCT_TYPE,
                productLine: trackingData.isHardwareOnly ? TRACKING_HARDWARE_ONLY : TRACKING_PRODUCT_LINE,
                productCategory,
                units: 1,
            }],
        },
    };

    return trackingPayload;
};
